import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { categoryService } from '../../services/api';
import styles from '../../pages/AdminPage.module.css';

/**
 * Grilla de categorías con drag & drop.
 * Al soltar: recalcula orden 10, 20, 30… y persiste vía POST /categorias/reordenar.
 * Optimista con rollback si falla el backend.
 */
export default function CategoriesGrid({ categories, onChange, onDelete }) {
  const [items, setItems] = useState(categories);
  const [saving, setSaving] = useState(false);

  // Mantener sincronizado si el padre recarga la lista
  if (categories !== items && !saving && categories.length !== items.length) {
    setItems(categories);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(c => c.id === active.id);
    const newIndex = items.findIndex(c => c.id === over.id);
    const nuevo = arrayMove(items, oldIndex, newIndex);

    // Optimista
    const previo = items;
    setItems(nuevo);
    setSaving(true);

    // Reasignar orden en múltiplos de 10 para mantener el patrón
    const payload = nuevo.map((c, i) => ({ id: c.id, orden: (i + 1) * 10 }));

    try {
      await categoryService.reorder(payload);
      onChange?.(); // opcional: pide al padre que recargue si quiere
    } catch (err) {
      alert('Error al reordenar: ' + err.message);
      setItems(previo); // rollback
    } finally {
      setSaving(false);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(c => c.id)} strategy={rectSortingStrategy}>
        <div className={styles.cardsGrid} style={{ opacity: saving ? 0.6 : 1, transition: 'opacity 150ms' }}>
          {items.map(cat => (
            <SortableCategoryCard key={cat.id} cat={cat} onDelete={onDelete} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableCategoryCard({ cat, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderLeftColor: cat.color,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
    boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,.15)' : undefined,
  };

  return (
    <div ref={setNodeRef} className={styles.categoryCard} style={style}>
      {/* Handle de arrastre */}
      <button
        {...attributes}
        {...listeners}
        title="Arrastra para reordenar"
        style={{
          background: 'transparent', border: 'none',
          cursor: 'grab', padding: 4, marginRight: 4,
          color: '#999', touchAction: 'none',
        }}
      >
        <GripVertical size={16} />
      </button>

      <span className={styles.categoryIcon}>{cat.icon}</span>
      <div>
        <h3>{cat.name}</h3>
        <p>{cat.description}</p>
      </div>
      <div className={styles.cardActions}>
        <Link to={`/admin/categoria/editar/${cat.id}`} className={styles.editBtn}>
          <Edit size={16} />
        </Link>
        <button onClick={() => onDelete?.(cat)}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
